import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ArrayHelpers, Forms, Loading, SystemText, Table } from 'e4shared';
import PageHeader from '../general/pageHeader';
import SortFilter from '../general/sortFilter';

import { setPrismMenu } from 'actions/ui';

import './simpleSpoof.css';

export const DefaultOptions = () => ({
    sortBy: 'name',
    ascending: true,
    showDelete: false,
    allowSetCurrent: true,
    page: 1,
    pageSize: 10,
    recordCount: 0,
    extraOptions: [],
});

export const SimpleSpoof = ({ options, columns, data, filterHelp, filterColumn }) => {
    const dispatch = useDispatch();
    const user = useSelector(state => state.user.user);

    React.useEffect(() => {
        // This page is only for PRISM partner/spoofing only
        if (!user.companySettings?.prism_UI || (!user?.partner_Id > 0 && !user?.spoofing)) {
            return null;
        }
    }, [user]);

    const [controlOptions, setControlOptions] = React.useState(options);
    const [search, setSearch] = React.useState('');
    const [filterToggle] = React.useState(true);

    const adminLoading = useSelector(state => state.admin.loading);

    React.useEffect(() => {
        dispatch(setPrismMenu('Spoof'));
    }, []);

    useEffect(() => {
        !controlOptions && setControlOptions(options);
    }, [options]);

    const sort = sortBy => {
        let ascending = true;

        if (controlOptions.sortBy === sortBy) {
            ascending = !controlOptions.ascending;
        }

        const options = {
            ...controlOptions,
            sortBy,
            ascending,
            page: 1,
        };

        setControlOptions(options);
    };

    const goToPage = page => {
        const options = {
            ...controlOptions,
            page,
        };

        setControlOptions(options);
    };

    const filter = value => {
        goToPage(1);
        setSearch(value);
    };

    const clean = s => (s ? s.toString().trim().toLowerCase() : '');
    const numberfy = s => (isNaN(s) ? 0 : parseFloat(s));

    const fieldIsCount = field => columns.some(c => c.showCount && c.dataProperty === field);
    const fieldIsNumeric = field => columns.some(c => c.isNumeric && c.dataProperty === field);
    const columnSortFunc = columns?.find(c => c.dataProperty === controlOptions.sortBy)?.sortByFunction;

    const compare = (a, b) => {
        if (fieldIsCount(controlOptions.sortBy)) {
            if (!a[controlOptions.sortBy]) a[controlOptions.sortBy] = [];
            if (!b[controlOptions.sortBy]) b[controlOptions.sortBy] = [];
            if (a[controlOptions.sortBy].length < b[controlOptions.sortBy].length) return controlOptions.ascending ? -1 : 1;
            if (a[controlOptions.sortBy].length > b[controlOptions.sortBy].length) return controlOptions.ascending ? 1 : -1;
        } else if (fieldIsNumeric(controlOptions.sortBy)) {
            if (numberfy(a[controlOptions.sortBy]) < numberfy(b[controlOptions.sortBy]))
                return controlOptions.ascending ? -1 : 1;
            if (numberfy(a[controlOptions.sortBy]) > numberfy(b[controlOptions.sortBy]))
                return controlOptions.ascending ? 1 : -1;
        } else {
            if (clean(a[controlOptions.sortBy]) < clean(b[controlOptions.sortBy])) return controlOptions.ascending ? -1 : 1;
            if (clean(a[controlOptions.sortBy]) > clean(b[controlOptions.sortBy])) return controlOptions.ascending ? 1 : -1;
        }
        return 0;
    };

    const toggledData = filterColumn
        ? data.filter(d => {
              return filterToggle === false || d[filterColumn] === filterToggle;
          })
        : data;

    const filteredData = search
        ? toggledData.filter(d => {
              let result = false;
              columns
                  .filter(c => !c.doNotSearch)
                  .forEach(c => {
                      if (c.searchPropertyIsArray && ArrayHelpers.existsAndHasLength(d[c.searchProperty])) {
                          d[c.searchProperty].map(x => {
                              result = result || (x || '').toLowerCase().indexOf(search.toLowerCase()) >= 0;
                          });
                      } else if (typeof d[c.dataProperty] === 'string') {
                          result =
                              result ||
                              d[c.dataProperty].toLowerCase().indexOf(search.toLowerCase()) >= 0 ||
                              (c.searchProperty &&
                                  (d[c.searchProperty] || '').toLowerCase().indexOf(search.toLowerCase()) >= 0);
                      } else {
                          result =
                              result ||
                              (c.searchProperty &&
                                  (d[c.searchProperty] || '').toLowerCase().indexOf(search.toLowerCase()) >= 0);
                      }
                  });

              return result;
          })
        : toggledData;

    controlOptions.recordCount = filteredData ? filteredData.length : controlOptions.recordCount;
    const start = (controlOptions.page - 1) * controlOptions.pageSize + 1;
    const end =
        start + controlOptions.pageSize > controlOptions.recordCount
            ? controlOptions.recordCount
            : start + controlOptions.pageSize - 1;

    const sortedData = columnSortFunc ? columnSortFunc(filteredData, controlOptions.ascending) : filteredData.sort(compare);

    const processedData = sortedData.reduce((result, d, i) => {
        if (i + 1 >= start && i + 1 <= end) result.push(d);
        return result;
    }, []);

    const filterFields = () => (
        <div className="row">
            <div className="col-md-12">
                <Forms.Text
                    id="filter-box"
                    data-testid="text-filter-field"
                    label={SystemText('Spoof/General/Search/Label')}
                    onChange={value => {
                        goToPage(1);
                        filter(value);
                    }}
                    placeholder={filterHelp || SystemText('Spoof/General/Search/Placeholder')}
                    value={search}
                    icon="fa-search"
                />
            </div>
        </div>
    );

    const pageTitle = () => {
        if (user.spoofing) {
            return SystemText('Spoof/Title/Spoofing', user.full_Name);
        }

        return SystemText('Spoof/Title');
    };

    return (
        <div data-testid="admin-title">
            <PageHeader title={pageTitle()} />
            <section className="content mtm10">
                <SortFilter filterFields={filterFields} filterBoxClassName="filter-raw" />
                <span data-testid="admin-table-area">
                    {adminLoading ? (
                        <Loading />
                    ) : (
                        <Table
                            data={processedData}
                            columns={columns.filter(c => !c.hide)}
                            options={controlOptions}
                            sort={column => sort(column)}
                            goToPage={page => goToPage(page)}
                        />
                    )}
                </span>
            </section>
        </div>
    );
};

export default SimpleSpoof;
